import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ChevronLeft, FileText, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES } from "@/constants/practice";

const MotionPracticePage = () => {
  const [form, setForm] = useState({
    motion_type: 'dismiss',
    case_type: 'contract_dispute',
    jurisdiction: 'us_federal',
    case_caption: '',
    facts: '',
    legal_arguments: [],
    relief_sought: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice/motion`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="motion-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Motion Practice</h1>
          <p className="text-muted-foreground">Generate motions to dismiss, summary judgment, compel & more</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Motion Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Motion Type</Label>
                      <Select value={form.motion_type} onValueChange={v => setForm(f => ({...f, motion_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dismiss">Motion to Dismiss</SelectItem>
                          <SelectItem value="summary_judgment">Motion for Summary Judgment</SelectItem>
                          <SelectItem value="compel">Motion to Compel</SelectItem>
                          <SelectItem value="sanctions">Motion for Sanctions</SelectItem>
                          <SelectItem value="preliminary_injunction">Preliminary Injunction</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Case Caption</Label>
                    <Input placeholder="Smith v. Jones, Case No. 2024-CV-12345" value={form.case_caption} onChange={e => setForm(f => ({...f, case_caption: e.target.value}))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Facts</Label>
                    <Textarea placeholder="Relevant facts supporting the motion..." value={form.facts} onChange={e => setForm(f => ({...f, facts: e.target.value}))} rows={4} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Relief Sought</Label>
                    <Input placeholder="e.g., Dismissal with prejudice, Summary judgment on all claims" value={form.relief_sought} onChange={e => setForm(f => ({...f, relief_sought: e.target.value}))} />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-slate-700 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-slate-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.facts}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><FileText className="h-5 w-5" /> Generate Motion</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-slate-700 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">{result.motion_name}</h2>
                <p className="text-white/80">{result.case_type} • {result.jurisdiction}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Motion</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
            
            {result.filing_checklist && (
              <Card>
                <CardHeader><CardTitle>Filing Checklist</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1">{result.filing_checklist.map((item, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />{item}</li>)}</ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default MotionPracticePage;
